import { z } from 'zod';

import { ShowcasePayload } from './showcase.js';

export const TalentCategory = z.enum(['skill', 'knowledge', 'toolchain', 'policy']);
export type TalentCategory = z.infer<typeof TalentCategory>;

// Version strings come from ARX (the validator of record). ARX nominally
// calls these SemVer but real data carries content hashes / 0.0.0
// placeholders, so the marketplace accepts any non-empty version rather
// than re-imposing a stricter format than the source of truth. See the
// matching note in tulpas.ts.
const SemverString = z.string().min(1).max(128);

const SlugString = z
  .string()
  .min(2)
  .max(64)
  .regex(/^[a-z0-9][a-z0-9-]*$/, 'lowercase alphanumeric with hyphens');

/**
 * Talent manifest — canonical arx-talents shape:
 *
 *   [metadata]
 *   name = "..."             # identifier (kebab; doubles as default slug)
 *   display_name = "..."     # optional pretty name
 *   version = "..."          # semver
 *   description = "..."
 *   category = "skill" | "knowledge" | "toolchain" | "policy"
 *
 *   [[instructions]]
 *   scope  = "skill" | "context" | "rule" | "policy" | "knowledge"
 *   source = "instructions/<file>.md"
 *
 * Optional `[resources.*]` and `[rules.*]` sub-tables are accepted as-is
 * so richer talents aren't rejected. The marketplace accepts both
 * `talent.toml` (canonical) and `manifest.json` / `talent.json`.
 *
 * Slug in the URL is the authoritative identifier — it does not need to
 * equal `metadata.name`. The upload route validates that
 * `metadata.category` matches the parent talent row.
 */
export const InstructionScope = z.enum(['skill', 'context', 'rule', 'policy', 'knowledge']);
export type InstructionScope = z.infer<typeof InstructionScope>;

export const InstructionEntry = z.object({
  scope: InstructionScope,
  source: z.string().min(1),
});
export type InstructionEntry = z.infer<typeof InstructionEntry>;

export const TalentMetadata = z.object({
  name: z.string().min(1).max(200),
  display_name: z.string().optional(),
  version: SemverString,
  description: z.string().max(2000).default(''),
  category: TalentCategory,
  authors: z.array(z.string()).default([]),
  tags: z.array(z.string()).max(20).default([]),
  license: z.string().optional(),
  homepage: z.string().url().optional(),
  repository: z.string().url().optional(),
});
export type TalentMetadata = z.infer<typeof TalentMetadata>;

export const TalentManifest = z.object({
  metadata: TalentMetadata,
  instructions: z.array(InstructionEntry).default([]),
  resources: z.record(z.unknown()).optional(),
  rules: z.record(z.unknown()).optional(),
});
export type TalentManifest = z.infer<typeof TalentManifest>;

export const TalentSummary = z.object({
  id: z.string(),
  slug: SlugString,
  name: z.string(),
  summary: z.string(),
  category: TalentCategory,
  owner_id: z.string(),
  owner_name: z.string().nullable(),
  latest_version: SemverString.nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type TalentSummary = z.infer<typeof TalentSummary>;

export const TalentDetail = TalentSummary.extend({
  description: z.string(),
  versions: z.array(
    z.object({
      id: z.string(),
      version: SemverString,
      size_bytes: z.number().int().nonnegative(),
      sha256: z.string(),
      created_at: z.string(),
    }),
  ),
  showcase: z.array(
    z.object({
      id: z.string(),
      talent_id: z.string(),
      position: z.number().int().nonnegative(),
      payload: ShowcasePayload,
      created_at: z.string(),
    }),
  ),
});
export type TalentDetail = z.infer<typeof TalentDetail>;

export const CreateTalentBody = z.object({
  slug: SlugString,
  name: z.string().min(1).max(200),
  summary: z.string().max(500).default(''),
  category: TalentCategory,
});
export type CreateTalentBody = z.infer<typeof CreateTalentBody>;

export const UpdateTalentBody = z.object({
  name: z.string().min(1).max(200).optional(),
  summary: z.string().max(500).optional(),
  description: z.string().max(20000).optional(),
});
export type UpdateTalentBody = z.infer<typeof UpdateTalentBody>;

export const ListTalentsQuery = z.object({
  q: z.string().optional(),
  category: TalentCategory.optional(),
  page: z.coerce.number().int().positive().default(1),
  per_page: z.coerce.number().int().min(1).max(100).default(20),
});
export type ListTalentsQuery = z.infer<typeof ListTalentsQuery>;
